/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React, { useEffect, useState, useReducer, useContext, useMemo, useCallback } from 'react'
import { Button, Divider, Header, Icon } from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import produce from 'immer'
import { notify } from 'react-notify-toast'
import { UltyModalContext } from '../Shared/UltyModal/UltyModalContext'
import ItemSelector from '../Items/ItemElements/ItemSelector'
import useModified from './useModified'
import { ItemList } from './ItemList/List/ItemList'
import {
    canSaveCategory,
    saveCategory,
    deleteCategory,
    getItemsFromCategory,
} from '../../services/Category/Category.service'
import { sortAsc } from '../../helpers/array'
import useUser from '../Shared/UserProvider/useUser'
import { UltyInputText } from '@ulty/ulty-ui'

const initialState = {
    category: {
        id: 0,
        name: '',
        description: '',
        position: 0,
        items: [],
    },
}

const useCategoryItemCache = () => {
    const [cache, setMap] = useState(new Map())
    const retrieve = async (categoryId) => {
        if (cache.has(categoryId)) {
            return cache.get(categoryId)
        }
        const items = await getItemsFromCategory(categoryId)
        setMap(new Map(cache.set(categoryId, items)))
        return items
    }
    return {
        retrieve,
        invalidate: () => setMap(new Map()),
    }
}


const categoryEditReducer = (state, action) => {
    switch (action.type) {
        case 'SET_CATEGORY':
            return produce(state, draft => {
                draft.category = {
                    id: action.payload.id,
                    name: action.payload.name || '',
                    description: action.payload.description || '',
                    position: action.payload.position,
                    items: action.payload.items,
                }
            })
        case 'SET_CATEGORY_FIELD':
            return produce(state, draft => {
                draft.category[action.payload.name] = action.payload.value
            })
        case 'SET_ITEMS':
            return produce(state, draft => {
                draft.category.items = action.payload.items
            })
        default:
            throw Error(`Action of categoryEditReducer: '${action.type}' not found`)
    }
}

const CategoryEdit = ({setId, slotId, category, onSaveCategory, onDeleteCategory, isParent}) => {
    const {user, canExtendCategories} = useUser()
    const [t] = useTranslation()
    const [state, dispatch] = useReducer(categoryEditReducer, initialState)
    const [isLoading, setIsLoading] = useState(false)
    const {handleUltyModal} = useContext(UltyModalContext)
    const {setModified} = useModified()

    const {retrieve, invalidate} = useCategoryItemCache()

    useEffect(() => {
        dispatch({type: 'SET_CATEGORY', payload: category ? {...category, items: []} : initialState.category})
    }, [category])

    useEffect(() => {
        (async function loadItems() {
            if (category?.id) {
                setIsLoading(true)
                const items = await retrieve(category.id)
                const sorted = sortAsc(items, i => i.position)
                setIsLoading(false)
                dispatch({type: 'SET_ITEMS', payload: {items: sorted}})
            }
        })()
    }, [category])

    const handleSaveCategory = async () => {
        setModified(false)
        setIsLoading(true)
        try {
            await saveCategory(setId, slotId, state.category)
            onSaveCategory()
            notify.show(t('global.registerSuccess'), 'success')
        } catch (e) {
            notify.show(`${t(`category_list.${e.message}`)}`, 'error')
        } finally {
            invalidate()
            setIsLoading(false)
        }
    }

    const handleDefineDeleteCategoryModal = async () => {
        const modalSettings = {
            title: t('category_edit.remove', {name: state.category.name}),
            content: <p>{t('category_edit.remove_confirm')}</p>,
            onValidate: async () => {
                const result = await deleteCategory(setId, slotId, state.category.id, false)
                if (result.code === 'OVERRIDES_EXIST') {
                    handleUltyModal(true, {
                        title: t('category_edit.category_has_overrides'),
                        content: <>
                            <p>{t('category_edit.following_pos_impacted')}</p>
                            <>
                                {result.pointOfSalesWithOverrides.map(pointOfSale => (
                                  <p key={pointOfSale.id}>{pointOfSale.name}</p>
                                ))}
                            </>
                        </>,
                        onValidate: async () => {
                            await deleteCategory(setId, slotId, state.category.id, true)
                            onDeleteCategory()
                            handleUltyModal(false)
                            setModified(false)
                        }
                    })
                } else {
                    onDeleteCategory()
                    handleUltyModal(false)
                    setModified(false)
                }
            },
        }
        handleUltyModal(true, modalSettings)
    }

    const deleteTemporaryCategory = () => {
        setModified(false)
        onDeleteCategory()
    }

    const handleAddItem = useCallback((itemToAdd) => {
        setModified(true)
        dispatch({
            type: 'SET_ITEMS',
            payload: {
                items: [...state.category.items, {
                    ...itemToAdd,
                    image: itemToAdd.images.find(image => image.isDefault)?.url,
                }],
            },
        })
    }, [setModified, state.category.items])

    const handleDeleteItem = (itemToDelete) => {
        setModified(true)
        dispatch({type: 'SET_ITEMS', payload: {items: state.category.items.filter(i => i.id !== itemToDelete.id)}})
    }

    const handleDeactivateItem = (itemToDeactivate) => {
        setModified(true)
        dispatch({
            type: 'SET_ITEMS',
            payload: {
                items: state.category.items.map(item => {
                    if (item.id === itemToDeactivate.id) {
                        return {
                            ...item,
                            activatedAt: null,
                        }
                    }
                    return item
                }),
            },
        })
    }

    const handleActivateItem = (itemToActivate) => {
        setModified(true)
        dispatch({
            type: 'SET_ITEMS',
            payload: {
                items: state.category.items.map(item => {
                    if (item.id === itemToActivate.id) {
                        return {
                            ...item,
                            activatedAt: new Date().toISOString(),
                        }
                    }
                    return item
                }),
            },
        })
    }

    const handleOnSortItems = (sortedItems) => {
        setModified(true)
        dispatch({type: 'SET_ITEMS', payload: {items: sortedItems}})
    }

    const handleDeleteCategory = async () => {
        if (!state.category.id) {
            deleteTemporaryCategory()
        } else {
            await handleDefineDeleteCategoryModal()
        }
    }

    const handleCategoryFieldChange = (name, value) => {
        setModified(true)
        dispatch({type: 'SET_CATEGORY_FIELD', payload: {name, value}})
    }

    const isCategoryOwnedByCurrentProvider = user.provider.id === category?.providerId || !category?.providerId

    return (<>
        <div css={css`
            display: flex;
            justify-content: space-between;
            column-gap: 1rem;
            z-index: 0 !important;
        `}>
            <div css={css`
                flex-grow: 1;
            `}>
                {isCategoryOwnedByCurrentProvider ? (
                  <UltyInputText
                    placeholder={t('category_edit.name')}
                    readonly={!isCategoryOwnedByCurrentProvider}
                    onChange={e => handleCategoryFieldChange('name', e.target.value)}
                    value={state.category.name}
                  />
                ) : <Header>{state.category.name}</Header>}

                {isCategoryOwnedByCurrentProvider ? (
                  <UltyInputText
                    placeholder={t('category_edit.description')}
                    readonly={!isCategoryOwnedByCurrentProvider}
                    onChange={e => handleCategoryFieldChange('description', e.target.value)}
                    value={state.category.description}
                  />
                ) : <p>{state.category.description}</p>}
            </div>

            <div css={css`
                display: flex;
                align-self: flex-start;
            `}>
                {(canExtendCategories() && isCategoryOwnedByCurrentProvider) && (
                  <Button
                    type="button"
                    color="red"
                    onClick={handleDeleteCategory}
                    loading={isLoading}
                  >
                      <Icon name={'delete'}/>
                      {t('global.delete')}
                  </Button>
                )}
                {canExtendCategories() && <Button
                  type="submit"
                  color="teal"
                  onClick={handleSaveCategory}
                  loading={isLoading}
                  disabled={!canSaveCategory(state.category)}
                >
                    {t('global.save')}
                </Button>}
            </div>
        </div>


        <div css={css`
            font-style: italic;
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: flex-end;
            padding: 10px 0;
        `}>
            {t('product_list.num_items', {n: `${state.category.items.length}`})}
        </div>

        <Divider/>

        {useMemo(() => {
            return (
              canExtendCategories() && <ItemSelector
                filters={{
                    types: ['MENU', 'PRODUCT'],
                }}
                excludedItemIds={state.category.items.map(i => i.id)}
                onItemAdd={handleAddItem}
              />
            )
        }, [state.category.items, handleAddItem])}

        <ItemList
          items={state.category.items}
          onDeleteItem={handleDeleteItem}
          onDeactivateItem={handleDeactivateItem}
          onActivateItem={handleActivateItem}
          onSortItems={handleOnSortItems}
          isParent={isParent}
        />
    </>)
}

CategoryEdit.propTypes = {
    digitalMerchantId: PropTypes.string,
    platformId: PropTypes.string,
    category: PropTypes.object,
    onSaveCategory: PropTypes.func.isRequired,
    onDeleteCategory: PropTypes.func.isRequired,
}

export default CategoryEdit
